import React, {
  memo, useContext, useEffect, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { withApollo } from 'react-apollo';

import { MapContext, OverlayView } from '@react-google-maps/api';

import { useRouter } from 'next/router';
import createCampsiteMarker from '../../lib/createCampsiteMarker';
import formatPrice from '../../lib/formatPrice';
import detectIE from '../../lib/detectIE';
import IbePropTypes from '../../IbePropTypes';
import { checkOpen } from '../../lib/helpers/availability';

import asyncComponent from '../../hocs/asyncComponent';
import TourMapMarkers from './TourMapMarkers';

import theme from '../../styles/config/theme';
import { ids as campsiteTypes } from '../../config/campsiteTypes';
import CAMPSITE_STATUS from '../../constants/campsiteStatus';
import { MARKER_Z_INDEX } from './constants';
import { MAX_SITES_VISIBLE_FOR_PRICES } from '../../lib/constants';
import AdvancedMarker from './AdvancedMarker';
import MarkerClusterer from './MarkerClusterer';
import { useMapContext } from './context';
import { filterVisibleMarkers } from './helpers';
import useMapBounds from '../../lib/hooks/useMapBounds';

const AsyncInfoWindow = asyncComponent(() => import('./InfoWindow'));

function CampsiteMapMarkers(props) {
  const router = useRouter();
  const {
    activePin,
    campsiteId,
    count,
    loading,
    goToTop,
    isMobile,
    pois,
    results,
    start,
    end,
    selectedEvent,
    isTours,
    ukItinerary,
    eventType,
  } = props;

  const { isSettled: isMapSettled } = useMapContext();
  /**
   * These parameters come from the custom clustering panel where CAMC
   * can tweak the parameters locally so they can get the clustering just right.
   * This can be seen by loading into /sites with the query param clusterControl=true
   * The params set in this panel are client-side, so require us to update it if needed
   */
  const gridSize = sessionStorage.getItem('gridSize');
  const minClusterSize = sessionStorage.getItem('minimumClusterSize');

  const pricesVisible = count <= MAX_SITES_VISIBLE_FOR_PRICES && isMapSettled;

  const mapInstance = useContext(MapContext);
  const bounds = useMapBounds(mapInstance);

  const visibleMarkers = useMemo(
    () => filterVisibleMarkers(results, bounds, activePin),
    [bounds, results, eventType, activePin],
  );

  const clustererRef = useRef();
  const poiClustererRef = useRef();

  useEffect(() => {
    if (clustererRef && clustererRef.current) {
      clustererRef.current.render();
    }
  }, [visibleMarkers.length, eventType]);

  useEffect(() => {
    if (poiClustererRef && poiClustererRef.current) {
      poiClustererRef.current.render();
    }
  }, [pois.length, eventType]);

  return (
    <>
      {!ukItinerary &&
        <MarkerClusterer
          gridSize={gridSize}
          minimumClusterSize={minClusterSize}
          key="poi-markers-clusterer"
          onLoad={clusterer => { poiClustererRef.current = clusterer; }}
        >
          {({ clusterer }) => pois.map((placeOfInterest) => (
            <AdvancedMarker
              icon={placeOfInterest.features?.[0]?.iconUrl}
              key={placeOfInterest.id}
              title={placeOfInterest.name}
              onClick={() => props.onMarkerClick(placeOfInterest)}
              position={{
                lat: placeOfInterest.lat,
                lng: placeOfInterest.lon,
              }}
              clusterer={clusterer}
              zIndex={pricesVisible
                ? MARKER_Z_INDEX.CAMPSITE_MARKER
                : MARKER_Z_INDEX.CAMPSITE_MARKER_NO_PRICE
              }
            >
              {!isMobile && activePin === placeOfInterest.id && (
              <OverlayView
                position={{
                  lat: placeOfInterest.lat,
                  lng: placeOfInterest.lon,
                }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <AsyncInfoWindow
                  campsiteId={placeOfInterest.id}
                  campsiteName={placeOfInterest.name}
                  goToTop={goToTop}
                  hasOffers={false}
                  isPoi
                  lat={placeOfInterest.lat}
                  lng={placeOfInterest.lon}
                  onCampsiteSearch={props.onCampsiteSearch}
                  onMoreDetailsClick={props.onMoreDetailsClick}
                  onQueryChange={props.onQueryChange}
                  selectedCampsiteId={campsiteId}
                  type={placeOfInterest.type}
                  zoomLevel={placeOfInterest.zoomLevel}
                  router={router}
                />
              </OverlayView>
              )}
            </AdvancedMarker>
          ))}
        </MarkerClusterer>}
      {!isTours ? (
        <MarkerClusterer
          gridSize={gridSize}
          minimumClusterSize={minClusterSize}
          key="campsite-markers-clusterer"
          onLoad={clusterer => { clustererRef.current = clusterer; }}
        >
          {cluster => visibleMarkers.map((campsite) => {
            const {
              cheapestMemberPrice,
              cheapestStandardPrice,
              offers,
              openDates,
            } = (campsite?.defaultEvent) ?? {};

            const active = [campsiteId, activePin].includes(campsite.id);
            let text = '';
            let length = 0;
            const isIE = detectIE();

            if (pricesVisible) {
              const poundHtmlEntity = '&#163;'; // Required for SVG
              const isOpen = checkOpen(openDates, start, end);
              const isCL = [
                campsiteTypes.UK_AFFILIATED_SITES,
                campsiteTypes.CERTIFICATED_LOCATIONS,
              ].includes(campsite.type);
              const defaultOpenText = isCL ? 'VIEW' : '...';
              const isFull = !isCL && !loading && cheapestMemberPrice === 0;
              text = isOpen ? (cheapestMemberPrice || (isFull ? CAMPSITE_STATUS.FULL : defaultOpenText)) : `${CAMPSITE_STATUS.CLOSED} `;
              length = text.length;

              if (cheapestMemberPrice && isOpen) {
                text = formatPrice(cheapestMemberPrice, poundHtmlEntity);
                length = `£${Number(cheapestMemberPrice).toFixed(2)}`.length;
              }
            }

            const isInfoWindowOpen = activePin === campsite.id;

            const campsiteIcon = createCampsiteMarker({
              active,
              color: theme[`COLOR_CAMPSITE_TYPE_${campsite.type}`],
              loading: !cheapestMemberPrice && pricesVisible && loading,
              length,
              text,
              isIE,
              isInfoWindowOpen,
            });

            /**
             * Each campsite needs a unique key based on both it's ID and
             * the selected event type. This is used as the React key for
             * each marker so React properly re-renders them when switching
             * event type
             */
            const campsiteKey = `${campsite.id}-${eventType}`;

            return (
              <AdvancedMarker
                icon={campsiteIcon}
                key={campsiteKey}
                onClick={() => props.onMarkerClick(campsite)}
                position={{
                  lat: campsite.lat,
                  lng: campsite.lon,
                }}
                title={typeof campsite.name === 'string' ? campsite.name : ''}
                clusterer={cluster}
                zIndex={pricesVisible
                  ? MARKER_Z_INDEX.CAMPSITE_MARKER
                  : MARKER_Z_INDEX.CAMPSITE_MARKER_NO_PRICE
                }
                text={text}
                eventType={eventType}
              >
                {!isMobile && isInfoWindowOpen && (
                <OverlayView
                  position={{
                    lat: campsite.lat,
                    lng: campsite.lon,
                  }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <AsyncInfoWindow
                    campsiteId={campsite.id}
                    campsiteName={campsite.name}
                    goToTop={goToTop}
                    hasOffers={offers ? !!offers.length : false}
                    isPoi={false}
                    lat={campsite.lat}
                    lng={campsite.lon}
                    memberPrice={cheapestMemberPrice}
                    selectedEvent={selectedEvent}
                    onCampsiteSearch={props.onCampsiteSearch}
                    onMoreDetailsClick={props.onMoreDetailsClick}
                    onQueryChange={props.onQueryChange}
                    standardPrice={cheapestStandardPrice || cheapestMemberPrice}
                    router={router}
                    selectedCampsiteId={campsiteId}
                    type={campsite.type}
                    zoomLevel={campsite.zoomLevel}
                  />
                </OverlayView>
                )}
              </AdvancedMarker>
            );
          })}
        </MarkerClusterer>) :
        <TourMapMarkers {...props} />}
    </>
  );
}

CampsiteMapMarkers.propTypes = {
  activePin: PropTypes.string,
  campsiteId: PropTypes.string,
  count: PropTypes.number,
  goToTop: PropTypes.func,
  isMobile: PropTypes.bool,
  loading: PropTypes.bool,
  onCampsiteSearch: PropTypes.func.isRequired,
  onMarkerClick: PropTypes.func.isRequired,
  onMoreDetailsClick: PropTypes.func.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  pois: PropTypes.arrayOf(PropTypes.shape(IbePropTypes.poi)),
  results: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    defaultEvent: PropTypes.shape(IbePropTypes.event),
    type: PropTypes.number,
    lat: PropTypes.number,
    lng: PropTypes.number,
    name: PropTypes.string,
    zoomLevel: PropTypes.number,
  })),
  start: PropTypes.string,
  end: PropTypes.string,
  selectedEvent: PropTypes.shape(IbePropTypes.event),
  defaultEvent: PropTypes.shape(IbePropTypes.event),
  tours: PropTypes.arrayOf(PropTypes.shape(IbePropTypes.tour)),
  isTours: PropTypes.bool,
  client: PropTypes.shape(IbePropTypes.client),
  ukItinerary: PropTypes.string,
  eventType: PropTypes.number.isRequired,
};

CampsiteMapMarkers.defaultProps = {
  activePin: '',
  campsiteId: '',
  count: null,
  goToTop() { },
  isMobile: false,
  loading: false,
  results: [],
  pois: [],
  tours: [],
  isTours: false,
  start: null,
  end: null,
  selectedEvent: null,
  defaultEvent: null,
  client: null,
  ukItinerary: '',
};

export default memo(withApollo(CampsiteMapMarkers));
